import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { eventsApi, paymentsApi } from '../../_lib/api-client';

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  // Stripe's confirmPayment redirect appends these to the return_url.
  const paymentIntentId = searchParams.get('payment_intent');
  const redirectStatus = searchParams.get('redirect_status');
  const eventId = searchParams.get('eventId');

  useEffect(() => {
    if (!eventId) return;

    const confirmAndRedirect = async () => {
      try {
        setIsRedirecting(true);

        if (paymentIntentId && redirectStatus === 'succeeded') {
          const result = await paymentsApi.confirmPaymentSuccess(eventId, paymentIntentId);
          if (!result.success) {
            setConfirmError(result.message || 'We could not confirm your payment. Please contact support.');
          }
        }

        // Wait 2 seconds to show the success message
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Fetch the event to get the code and name for the URL
        const event = await eventsApi.getById(eventId);
        if (event) {
          const eventNameSlug = event.eventName.toLowerCase().replace(/\s+/g, '-');
          navigate(`/app/events/${event.code}/${eventNameSlug}`, {
            replace: true,
            state: { paymentSuccess: true } // Pass flag to trigger refresh
          });
        } else {
          // Fallback to events page if event not found
          navigate('/app/events', { replace: true });
        }
      } catch (error) {
        console.error('Failed to confirm payment / redirect to event:', error);
        // Fallback to events page on error
        navigate('/app/events', { replace: true });
      }
    };

    confirmAndRedirect();
  }, [eventId, paymentIntentId, redirectStatus, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 py-12">
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          {/* Success Message */}
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
          <p className="text-xl text-gray-600 mb-8">
            Your event has been upgraded to unlimited guests
          </p>
          
          {/* Redirecting Message */}
          {isRedirecting && eventId && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <p className="text-blue-800 font-medium">
                  Redirecting you back to your event page...
                </p>
              </div>
            </div>
          )}

          {/* Confirmation Error */}
          {confirmError && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-amber-800 text-sm">{confirmError}</p>
            </div>
          )}

          {/* What's Next */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
            <h2 className="font-semibold text-gray-900 mb-3">What happens next?</h2>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>You can now invite unlimited guests to your wedding</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>All premium features are now unlocked</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>You'll receive a receipt from Stripe via email</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Stripe handles all tax compliance and invoicing</span>
              </li>
            </ul>
          </div>

          {/* Transaction ID */}
          {paymentIntentId && (
            <div className="mb-6 text-sm text-gray-500">
              Transaction ID: {paymentIntentId}
            </div>
          )}

          {/* Actions - Only show if not auto-redirecting */}
          {!isRedirecting && (
            <div className="space-y-3">
              {eventId ? (
                <button
                  onClick={async () => {
                    try {
                      const event = await eventsApi.getById(eventId);
                      if (event) {
                        const eventNameSlug = event.eventName.toLowerCase().replace(/\s+/g, '-');
                        navigate(`/app/events/${event.code}/${eventNameSlug}`);
                      } else {
                        navigate('/app/events');
                      }
                    } catch (error) {
                      navigate('/app/events');
                    }
                  }}
                  className="w-full bg-gradient-to-r from-rose-500 to-pink-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-rose-600 hover:to-pink-700 transition-all"
                >
                  Return to Event
                </button>
              ) : (
                <button
                  onClick={() => navigate('/app/events')}
                  className="w-full bg-gradient-to-r from-rose-500 to-pink-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-rose-600 hover:to-pink-700 transition-all"
                >
                  Continue to Dashboard
                </button>
              )}
              <button
                onClick={() => navigate('/app/events')}
                className="w-full bg-gray-100 text-gray-700 font-semibold py-3 px-6 rounded-lg hover:bg-gray-200 transition-colors"
              >
                View All Events
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;